import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';

import dayjs from 'dayjs';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import ProductForm, { ModifierGroupRef, ProductDraft, draftToPayload, emptyDraft, productErrorMessage } from './ProductForm';
import { ProductMediaPicker, uploadProductMedia } from './ProductMedia';
import { SectionCard, EmptyState } from './ui';
import { Category, Supplier } from '../types';
import { moduleOn } from '../utils/features';
import { ShellContext } from './layout/AppShell';

const AddProduct = () => {
  const axios = useAxios();
  const navigate = useNavigate();
  // Initial stock is for businesses that keep inventory (Configuración › Tu negocio).
  const { features } = React.useContext(ShellContext);

  const [draft, setDraft] = React.useState<ProductDraft>(emptyDraft);
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [suppliers, setSuppliers] = React.useState<Supplier[]>([]);
  const [locations, setLocations] = React.useState<any[]>([]);
  const [stock, setStock] = React.useState<Record<number, string>>({});
  const [mediaFiles, setMediaFiles] = React.useState<File[]>([]);
  const [modifierGroups, setModifierGroups] = React.useState<ModifierGroupRef[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    axios.get('api/categories/').then(r => setCategories(r.data ?? [])).catch(() => {});
    axios.get('inventory/suppliers/').then(r => setSuppliers(r.data ?? [])).catch(() => {});
    axios.get('api/locations/').then(r => setLocations(r.data ?? [])).catch(() => {});
    axios.get('api/modifier-groups/').then(r => setModifierGroups(r.data ?? [])).catch(() => {});
  }, [axios]);

  const save = async (addAnother: boolean) => {
    // Without a barcode the server makes one of the business's own (Receipt-Web-Server api/barcodes.py).
    if (!draft.name.trim()) {
      setError('El nombre es obligatorio.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { data: product } = await axios.post('api/products/', draftToPayload(draft));

      // Initial stock is recorded as inventory adjustments so it shows up in the movement history.
      const initial = Object.entries(stock).filter(([, qty]) => Number(qty) > 0);
      if (initial.length > 0) {
        await axios.get('inventory/inventory/', { params: { product_id: product.id } }); // ensures inventory rows exist
        await Promise.all(initial.map(([location, qty]) =>
          axios.post('inventory/inventory-log/', {
            product: product.id, location: Number(location), change: Number(qty), reason: 'Stock inicial', date: dayjs().format(),
          }),
        ));
      }

      // The product exists now; a photo that fails to upload can be added again from its page.
      const failed: string[] = [];
      for (const file of mediaFiles) {
        try {
          await uploadProductMedia(axios, product.id, file);
        } catch {
          failed.push(file.name);
        }
      }

      if (addAnother) {
        setDraft({ ...emptyDraft, category: draft.category, suppliers: draft.suppliers, modifier_groups: draft.modifier_groups });
        setStock({});
        setMediaFiles([]);
        setSaving(false);
        if (failed.length) setError(`Producto guardado, pero no se subieron: ${failed.join(', ')}. Agrégalos desde la página del producto.`);
      } else {
        navigate(`/admin/products/${product.id}`, { replace: true, state: failed.length ? { mediaFailed: failed } : undefined });
      }
    } catch (err) {
      setError(productErrorMessage(err));
      setSaving(false);
    }
  };

  return (
    <PageShell
      back={{ to: '/admin/products', label: 'Productos' }}
      title="Nuevo producto"
      actions={
        <>
          <Button color="inherit" onClick={() => save(true)} disabled={saving}>Guardar y agregar otro</Button>
          <Button variant="contained" onClick={() => save(false)} disabled={saving}>{saving && mediaFiles.length ? 'Subiendo fotos…' : 'Guardar producto'}</Button>
        </>
      }
    >
      <form onSubmit={e => { e.preventDefault(); save(false); }}>
        <ProductForm
          draft={draft}
          onChange={setDraft}
          categories={categories}
          suppliers={suppliers}
          error={error}
          media={<ProductMediaPicker files={mediaFiles} onChange={setMediaFiles} />}
          modifierGroups={modifierGroups}
          extra={moduleOn(features, 'inventory') && (
            <SectionCard title="Stock inicial" subtitle="Unidades disponibles hoy en cada sucursal. Puedes ajustarlo después en Inventario.">
              {locations.length === 0 ? (
                <EmptyState compact title="Aún no tienes sucursales" description="Crea una sucursal en Organización para llevar inventario." />
              ) : (
                <Grid container spacing={2}>
                  {locations.map(loc => (
                    <Grid key={loc.id} size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label={loc.name}
                        type="number"
                        fullWidth
                        value={stock[loc.id] ?? ''}
                        onChange={e => setStock(s => ({ ...s, [loc.id]: e.target.value }))}
                        slotProps={{ input: { endAdornment: <InputAdornment position="end">uds.</InputAdornment> }, htmlInput: { min: 0, step: 'any' } }}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </SectionCard>
          )}
        />
        <button type="submit" hidden />
      </form>
    </PageShell>
  );
};

export default AddProduct;
